import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonBlock, SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";

/**
 * The Virtual Firewall detail while its first answer is on its way (CTX-1396): the same
 * sections, in the same cards and at the same breakpoints as the page (FwaasDetails), so
 * nothing jumps when the data arrives. One busy region announces the load; every bar
 * inside it is decorative.
 */

/** The page's card, with a title and a subtitle placeholder. */
function SkeletonCard({ section, children }: { section: string; children: React.ReactNode }) {
    return (
        <GenericCard isCollapsible={false} variant="primary" shadow="sm" className="h-full">
            <div className="p-5 flex flex-col gap-4" data-skeleton-section={section}>
                <div className="flex flex-col gap-2">
                    <SkeletonLine w={140} h={16} decorative />
                    <SkeletonLine w="55%" h={12} decorative />
                </div>
                {children}
            </div>
        </GenericCard>
    );
}

function HeaderSkeleton() {
    return (
        <div className="flex flex-wrap items-start justify-between gap-3" data-skeleton-section="header">
            <div className="flex flex-col gap-2">
                <SkeletonLine w={110} h={12} decorative />
                <div className="flex items-center gap-3">
                    <SkeletonLine w={260} h={22} decorative />
                    <SkeletonBlock w={60} h={22} roundedClass="rounded-full" decorative />
                </div>
                <SkeletonLine w={340} h={12} decorative />
            </div>
            <div className="flex items-center gap-3">
                <SkeletonBlock w={170} h={26} roundedClass="rounded-full" decorative />
                <SkeletonLine w={90} h={12} decorative />
            </div>
        </div>
    );
}

/** A node of the map: an icon and two lines, the size of the real card. */
function NodeSkeleton() {
    return (
        <div className="flex items-center gap-2.5 rounded-xl border border-semantic-border-primary px-3 py-2 w-full max-w-[204px]">
            <SkeletonBlock w={30} h={30} roundedClass="rounded-lg" decorative />
            <div className="flex flex-col gap-1.5 flex-1">
                <SkeletonLine w="70%" h={12} decorative />
                <SkeletonLine w="50%" h={10} decorative />
            </div>
        </div>
    );
}

function MapSkeleton() {
    const lane = (
        <div className="flex flex-col items-center gap-4 rounded-2xl bg-semantic-bg-secondary p-4">
            <SkeletonLine w={110} h={10} decorative />
            {[0, 1, 2].map((i) => (
                <NodeSkeleton key={i} />
            ))}
        </div>
    );
    return (
        <SkeletonCard section="map">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-6">
                {lane}
                <SkeletonBlock w={240} h={110} roundedClass="rounded-xl" className="mx-auto" decorative />
                {lane}
            </div>
        </SkeletonCard>
    );
}

function AttentionSkeleton() {
    return (
        <SkeletonCard section="attention">
            {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-start gap-3">
                    <SkeletonBlock w={76} h={22} roundedClass="rounded-full" decorative />
                    <div className="flex flex-col gap-1.5 flex-1">
                        <SkeletonLine w="65%" h={14} decorative />
                        <SkeletonLine w="85%" h={12} decorative />
                    </div>
                </div>
            ))}
        </SkeletonCard>
    );
}

function HealthSkeleton() {
    return (
        <SkeletonCard section="health">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex flex-col gap-1.5">
                        <SkeletonLine w="50%" h={12} decorative />
                        <SkeletonLine w="40%" h={22} decorative />
                        <SkeletonLine w="75%" h={10} decorative />
                    </div>
                ))}
            </div>
        </SkeletonCard>
    );
}

/** A table: a header strip and `rows` rows. */
function TableSkeleton({ rows }: { rows: number }) {
    return (
        <div className="flex flex-col gap-3">
            <SkeletonBlock h={28} roundedClass="rounded" decorative />
            {Array.from({ length: rows }, (_, i) => (
                <SkeletonLine key={i} w={`${90 - (i % 3) * 12}%`} h={14} decorative />
            ))}
        </div>
    );
}

export default function FwaasDetailsSkeleton() {
    return (
        <div className="flex flex-col gap-4" role="status" aria-busy aria-label="Loading Virtual Firewall details">
            <HeaderSkeleton />
            <MapSkeleton />
            {/* Same rows and breakpoints as FwaasDetails. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <AttentionSkeleton />
                <HealthSkeleton />
            </div>
            <div className="grid grid-cols-1 min-[1440px]:grid-cols-3 gap-4">
                <div className="min-[1440px]:col-span-2 min-w-0">
                    <SkeletonCard section="policies">
                        <SkeletonBlock w={220} h={30} roundedClass="rounded-full" decorative />
                        <TableSkeleton rows={5} />
                    </SkeletonCard>
                </div>
                <SkeletonCard section="licenses">
                    <TableSkeleton rows={6} />
                </SkeletonCard>
            </div>
        </div>
    );
}
